import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { BookOpen, Megaphone, ScrollText, Tv, Check, ChevronLeft, ChevronRight, FileText, Minus, Plus, X, Vote } from 'lucide-react';
import { abrirDocumento } from './TodosProjetos';
import { PareceresResumo } from './ComissoesVereador'; // PLENARIUM:COMISSOES
import LeitorPDF from './LeitorPDF'; // PLENARIUM:LEITOR_PDF
import { ChamadaSecretario } from './Chamadas'; // PLENARIUM:DUAS_CHAMADAS -- passo Chamada (so leitura)

// =====================================================================
// PLENARIUM:LEITURAS -- tela "Leituras" do 1o e do 2o Secretario.
// Tres passos, na ordem da sessao:
//   1. Ata anterior  -> o PDF anexado no Tramite, lido na propria tela
//   2. Expediente    -> itens cadastrados pelo admin (materia apresentada,
//                       comunicado, convite, justificativa, aviso, oficio...); "Ler agora" abre o texto
//                       em letra grande e mostra o item na TV
//   3. Ordem do Dia  -> materias da pauta; "So o titulo" ou "Na integra"
// O que esta na TV aparece no alto, com o botao "Tirar da TV".
// =====================================================================

export const SECRETARIOS = ['PRIMEIRO_SECRETARIO', 'SEGUNDO_SECRETARIO'];
export const podeLer = (cargo) => SECRETARIOS.includes(cargo);
export function cargoMesa(cargo, sexo) {
  const f = sexo === 'F';
  switch (cargo) {
    case 'PRESIDENTE': return 'Presidente';
    case 'VICE_PRESIDENTE': return 'Vice-Presidente';
    case 'SEGUNDO_VICE_PRESIDENTE': return '2º Vice-Presidente';
    case 'PRIMEIRO_SECRETARIO': return f ? '1ª Secretária' : '1º Secretário';
    case 'SEGUNDO_SECRETARIO': return f ? '2ª Secretária' : '2º Secretário';
    case 'TESOUREIRO': return f ? 'Tesoureira' : 'Tesoureiro';
    default: return f ? 'Vereadora' : 'Vereador';
  }
}
export const TIPO_ITEM = { MATERIA: 'Matéria apresentada', COMUNICADO: 'Comunicado', CONVITE: 'Convite', JUSTIFICATIVA: 'Justificativa de ausência', AVISO: 'Aviso', OFICIO: 'Ofício', OUTRO: 'Outro' };
const COR_ITEM = { MATERIA: ['#1d4ed8', '#dbeafe'], AVISO: ['#b45309', '#fef3c7'], CONVITE: ['#7c3aed', '#ede9fe'], OFICIO: ['#0f766e', '#ccfbf1'], COMUNICADO: ['#be185d', '#fce7f3'], OUTRO: ['#475569', '#f1f5f9'], JUSTIFICATIVA: ['#c2410c', '#ffedd5'] };
const TIPO_PAUTA = {
  PROJETO_LEI: 'Projeto de Lei', PROJETO_RESOLUCAO: 'Projeto de Resolução', REQUERIMENTO: 'Requerimento', MOCAO: 'Moção',
  INDICACAO: 'Indicação', OUTROS: 'Outros', ATA_ANTERIOR: 'Ata da Sessão Anterior'
};
const STATUS_PAUTA = { PENDENTE: 'Aguardando', EM_VOTACAO: 'Em votação', APROVADA: 'Aprovada', REPROVADA: 'Reprovada', RETIRADA: 'Retirada' };
const ANEXO = { PROJETO: 'Projeto', PARECER_COMISSAO: 'Parecer da Comissão', PARECER_RELATOR: 'Parecer do Relator', EMENDA: 'Emenda', SUBSTITUTIVO: 'Substitutivo', REDACAO_FINAL: 'Redação Final', OFICIO: 'Ofício', OUTROS: 'Documento' };

const CSS = `
.pls { max-width: 980px; margin: 0 auto; padding: 4px 2px 40px; color: #0f172a; }
.pls-cab { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; }
.pls-cab-ic { width: 52px; height: 52px; border-radius: 16px; background: #e0e7ff; color: #3730a3; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.pls-cab b { display: block; font-size: 1.35rem; font-weight: 800; line-height: 1.15; }
.pls-cab small { display: block; color: #64748b; font-size: .92rem; margin-top: 2px; }
.pls-natv { display: flex; align-items: center; gap: 12px; background: #0f2a5f; color: #fff; border-radius: 14px; padding: 12px 14px; margin-bottom: 14px; }
.pls-natv-ic { width: 38px; height: 38px; border-radius: 10px; background: rgba(255,255,255,.14); display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.pls-natv-txt { flex: 1; min-width: 0; }
.pls-natv-txt small { display: block; font-size: .7rem; font-weight: 800; letter-spacing: 1.6px; color: #93c5fd; }
.pls-natv-txt span { display: block; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pls-bt { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 46px; padding: 0 16px; border-radius: 12px; border: 1px solid #cbd5e1; background: #fff; color: #0f172a; font: inherit; font-weight: 700; font-size: .95rem; cursor: pointer; }
.pls-bt:active { transform: scale(.98); }
.pls-bt[disabled] { opacity: .5; cursor: default; }
.pls-bt-azul { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.pls-bt-verde { background: #15803d; border-color: #15803d; color: #fff; }
.pls-bt-tv { background: #0f2a5f; border-color: #0f2a5f; color: #fff; }
.pls-bt-claro { background: rgba(255,255,255,.14); border-color: rgba(255,255,255,.3); color: #fff; min-height: 40px; }
.pls-passos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
.pls-passo { display: flex; align-items: center; gap: 10px; text-align: left; padding: 12px; border-radius: 14px; border: 2px solid #e2e8f0; background: #fff; cursor: pointer; font: inherit; color: #0f172a; min-width: 0; }
.pls-passo.ativo { border-color: #1d4ed8; background: #eff6ff; }
.pls-num { width: 34px; height: 34px; border-radius: 50%; background: #e2e8f0; color: #334155; font-weight: 900; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.pls-passo.ativo .pls-num { background: #1d4ed8; color: #fff; }
.pls-passo b { display: block; font-size: 1rem; line-height: 1.15; }
.pls-passo small { display: block; color: #64748b; font-size: .78rem; margin-top: 2px; }
.pls-dica { color: #475569; font-size: .92rem; margin: 0 0 12px; line-height: 1.45; }
.pls-lista { display: flex; flex-direction: column; gap: 10px; }
.pls-item { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 12px 14px; }
.pls-item.lido { background: #f8fafc; }
.pls-item.lido .pls-item-tit { color: #64748b; }
.pls-item.votando { border: 2px solid #dc2626; }
.pls-ordem { width: 30px; height: 30px; border-radius: 50%; background: #f1f5f9; color: #334155; font-weight: 800; font-size: .9rem; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.pls-item.lido .pls-ordem { background: #dcfce7; color: #15803d; }
.pls-item-txt { flex: 1; min-width: 0; }
.pls-chip { display: inline-block; font-size: .72rem; font-weight: 800; letter-spacing: .4px; padding: 3px 9px; border-radius: 999px; margin-right: 6px; }
.pls-item-tit { display: block; font-weight: 700; font-size: 1.02rem; line-height: 1.3; margin-top: 4px; overflow-wrap: anywhere; }
.pls-vazio { background: #fff; border: 1px dashed #cbd5e1; border-radius: 14px; padding: 22px; text-align: center; color: #64748b; }
.pls-cartao { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 18px; }
.pls-cartao h3 { margin: 0 0 4px; font-size: 1.2rem; }
.pls-docs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; }
.pls-aviso { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; border-radius: 12px; padding: 10px 12px; margin-top: 12px; font-size: .92rem; }
/* Leitor (letra grande) */
.pls-leitor { position: fixed; inset: 0; z-index: 9000; background: #fffdf7; color: #111827; display: flex; flex-direction: column; }
.pls-leitor-topo { display: flex; align-items: center; gap: 10px; padding: calc(10px + env(safe-area-inset-top)) 14px 10px; background: #0f2a5f; color: #fff; }
.pls-leitor-topo .pls-rot { flex: 1; min-width: 0; font-weight: 800; font-size: .8rem; letter-spacing: 1.6px; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pls-zoom { display: inline-flex; gap: 6px; }
.pls-leitor-corpo { flex: 1; min-height: 0; overflow-y: auto; padding: 22px clamp(18px, 5vw, 64px) 30px; }
.pls-leitor-corpo h2 { margin: 0 0 14px; font-weight: 800; line-height: 1.25; }
.pls-leitor-corpo .pls-texto { white-space: pre-wrap; line-height: 1.6; text-align: justify; }
.pls-leitor-meta { color: #64748b; font-weight: 700; margin-bottom: 8px; }
.pls-leitor-base { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px 14px calc(12px + env(safe-area-inset-bottom)); border-top: 1px solid #e5e7eb; background: #fff; }
.pls-leitor-base .pls-bt { flex: 1 1 180px; min-height: 54px; font-size: 1rem; }
.pls-seg { display: inline-flex; background: #e2e8f0; border-radius: 12px; padding: 4px; margin-bottom: 16px; }
.pls-seg button { border: 0; background: transparent; font: inherit; font-weight: 800; padding: 10px 18px; border-radius: 9px; cursor: pointer; color: #334155; }
.pls-seg button.ativo { background: #fff; color: #1d4ed8; box-shadow: 0 1px 3px rgba(0,0,0,.12); }
@media (max-width: 640px) {
  .pls-passos { grid-template-columns: 1fr; }
  .pls-passo { padding: 10px 12px; }
  .pls-item { flex-wrap: wrap; }
  .pls-item .pls-bt { flex: 1 1 100%; }
}
`;

const aberta = (p) => p && p.status !== 'RETIRADA';
let sockLeituras = null;

// >>> PLENARIUM:FRASE_MATERIA -- a materia como o Secretario le em voz alta:
// "Projeto de Lei nº 15/2026, de autoria do Vereador Francisco, que institui..."
export function autoriaFrase(autor, vereadores = []) {
  const a = String(autor || '').trim();
  if (!a) return '';
  const sexo = (nome) => { const n = String(nome || '').trim().toLowerCase(); const v = (vereadores || []).find(x => [x.nomeParlamentar, x.nome].filter(Boolean).some(y => y.trim().toLowerCase() === n)); return v ? v.sexo : null; };
  let m = a.match(/^Vereador(?:a|es|as)?\s+(.+)$/i) || a.match(/^Ver\.\s+(.+)$/i);
  if (/^Vereador(?:es|as)\s/i.test(a) || (m && /,|\se\s/.test(m[1]) && /^Vereador(?:es|as)/i.test(a))) {
    const lista = a.replace(/^Vereador(?:es|as)\s+/i, '');
    const nomes = lista.split(/,\s*|\s+e\s+/);
    const todasF = nomes.length > 0 && nomes.every(n => sexo(n) === 'F');
    return `de autoria ${todasF ? 'das Vereadoras' : 'dos Vereadores'} ${lista}`;
  }
  if (m) { const f = /^Vereadora/i.test(a) || sexo(m[1]) === 'F'; return `de autoria ${f ? 'da Vereadora' : 'do Vereador'} ${m[1]}`; }
  if (/^Iniciativa Popular/i.test(a)) { const r = a.replace(/^Iniciativa Popular\s*[—–-]?\s*/i, ''); return r ? `de iniciativa popular (${r})` : 'de iniciativa popular'; }
  if (/^(Mesa|Comiss|C[aâ]mara|Procuradoria|Secretaria)/i.test(a)) return `de autoria da ${a}`;
  if (/^(Poder|Executivo|Prefeito|Gabinete|Legislativo)/i.test(a)) return `de autoria do ${a}`;
  const s = sexo(a);
  if (s) return `de autoria ${s === 'F' ? 'da Vereadora' : 'do Vereador'} ${a}`;
  return `de autoria de ${a}`;
}
const VERBOS_EMENTA = /^(institui|disp[õo]e|altera|autoriza|cria|denomina|concede|requer|indica|solicita|declara|estabelece|revoga|fixa|pro[ií]be|regulamenta|inclui|define|determina|reconhece|acrescenta|modifica|aprova|homologa|torna|considera|outorga|d[aá]|apresenta|manifesta|congratula|parabeniza|presta|reajusta|abre|assegura|garante|veda|obriga|implanta|institucionaliza|oficializa|confere|suspende|prorroga|ratifica|designa|nomeia|atualiza|consolida|reestrutura|amplia|regula|estima|organiza)$/i;
export function fraseMateria({ tipo, numero, ano, autor, ementa }, vereadores = []) {
  const n0 = String(numero || '').replace(/^[A-Za-zÀ-ÿ]+\.?\s+/, '').trim();
  const num = (n0.includes('/') || !ano ? n0 : `${n0}/${ano}`).replace(/^0+(\d)/, '$1');
  const au = autoriaFrase(autor, vereadores);
  let em = String(ementa || '').trim().replace(/[.;,\s]+$/, '');
  let liga = ', ';
  if (em) {
    const caps = em === em.toUpperCase();
    if (VERBOS_EMENTA.test(em.split(/\s+/)[0])) em = 'que ' + (caps ? em : em[0].toLowerCase() + em.slice(1));
    else liga = ' — ';
  }
  const cab = [`${tipo || 'Matéria'}${num ? ` nº ${num}` : ''}`, au].filter(Boolean).join(', ');
  return em ? `${cab}${liga}${em}.` : `${cab}.`;
}
// <<< PLENARIUM:FRASE_MATERIA

export default function LeiturasSecretario({ sessao, pautas = [], pautaAtiva = null, usuario = null, cargo = null }) {
  const sessaoId = sessao && sessao.status === 'EM_ANDAMENTO' ? sessao.id : null;
  const [passo, setPasso] = useState(() => { try { return sessionStorage.getItem('pls-passo') || 'chamada'; } catch (e) { return 'chamada'; } });
  const [itens, setItens] = useState([]);
  // PLENARIUM:ATAS_VARIAS -- todas as atas da sessao; o Secretario le uma de cada vez
  const [atasLista, setAtas] = useState(null);
  const [ataIdx, setAtaIdx] = useState(0);
  const ata = atasLista === null ? null : atasLista.erro ? { erro: true } : (atasLista[Math.min(ataIdx, Math.max(atasLista.length - 1, 0))] || {});
  const varias = Array.isArray(atasLista) && atasLista.length > 1;
  const tituloCurto = (a) => (a && a.projeto ? `Ata nº ${a.projeto.numero}/${a.projeto.ano}` : (a && a.pauta && (a.pauta.titulo || a.pauta.numero)) || 'Ata');
  const [docAta, setDocAta] = useState(0); // PLENARIUM:LEITOR_PDF
  const [naTV, setNaTV] = useState(null);
  const [leitor, setLeitor] = useState(null); // { tipo: 'item'|'materia', id }
  const [integra, setIntegra] = useState({});
  const [modoMateria, setModoMateria] = useState('titulo');
  const [fonte, setFonte] = useState(() => { try { return Number(localStorage.getItem('pls-fonte')) || 26; } catch (e) { return 26; } });
  const [ocupado, setOcupado] = useState(false);
  const [concluido, setConcluido] = useState(false); // PLENARIUM:EXPEDIENTE_CONCLUIDO -- tela de leitura concluida
  const leitorNome = `${cargoMesa(cargo || (usuario && usuario.cargo), usuario && usuario.sexo)} ${(usuario && (usuario.nomeParlamentar || usuario.nome)) || ''}`.trim();
  const ultimoAtual = useRef(null);
  // PLENARIUM:FRASE_MATERIA -- vereadores (sexo) para "do Vereador" / "da Vereadora"
  const [vereadoresF, setVereadoresF] = useState([]);
  useEffect(() => { axios.get('/api/vereadores').then(r => setVereadoresF(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  useEffect(() => { try { sessionStorage.setItem('pls-passo', passo); } catch (e) { /* segue */ } }, [passo]);
  useEffect(() => { try { localStorage.setItem('pls-fonte', String(fonte)); } catch (e) { /* segue */ } }, [fonte]);

  const carregarItens = useCallback(() => {
    if (!sessaoId) { setItens([]); return; }
    axios.get(`/api/leituras/sessao/${sessaoId}/expediente`).then(r => setItens(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [sessaoId]);
  const carregarAtual = useCallback(() => {
    if (!sessaoId) { setNaTV(null); return; }
    axios.get(`/api/leituras/sessao/${sessaoId}/atual`).then(r => setNaTV(r.data || null)).catch(() => {});
  }, [sessaoId]);

  useEffect(() => {
    carregarItens(); carregarAtual();
    if (!sessaoId) { setAtas(null); return undefined; }
    axios.get(`/api/leituras/sessao/${sessaoId}/atas-anteriores`).then(r => setAtas(Array.isArray(r.data && r.data.atas) ? r.data.atas : [])) // PLENARIUM:ATAS_VARIAS
      .catch(() => axios.get(`/api/leituras/sessao/${sessaoId}/ata-anterior`).then(r => setAtas(r.data && r.data.projeto ? [r.data] : [])).catch(() => setAtas({ erro: true })));
    if (!sockLeituras) sockLeituras = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    const s = sockLeituras;
    const entrar = () => { s.emit('entrar-sala', `sessao-${sessaoId}`); carregarItens(); carregarAtual(); };
    const aoExp = (d) => { if (!d || d.sessaoId === sessaoId) carregarItens(); };
    const aoAtual = (d) => { if (d && d.sessaoId === sessaoId) setNaTV(d.leitura || null); };
    if (s.connected) s.emit('entrar-sala', `sessao-${sessaoId}`);
    s.on('connect', entrar); s.on('expediente-atualizado', aoExp); s.on('leitura-atual', aoAtual);
    const voltou = () => { if (document.visibilityState === 'visible') { carregarItens(); carregarAtual(); } };
    document.addEventListener('visibilitychange', voltou);
    return () => { s.off('connect', entrar); s.off('expediente-atualizado', aoExp); s.off('leitura-atual', aoAtual); document.removeEventListener('visibilitychange', voltou); };
  }, [sessaoId, carregarItens, carregarAtual]);

  // ---- TV ----
  const mostrarNaTV = async (dados) => {
    if (!sessaoId) return;
    try {
      const r = await axios.post(`/api/leituras/sessao/${sessaoId}/atual`, { ...dados, leitor: leitorNome });
      ultimoAtual.current = dados.chave; setNaTV(r.data);
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Não foi possível mostrar na TV.'); }
  };
  const tirarDaTV = async () => {
    if (!sessaoId) return;
    try { await axios.post(`/api/leituras/sessao/${sessaoId}/atual`, { limpar: true }); setNaTV(null); } catch (e) { toast.error('Não foi possível tirar da TV.'); }
  };
  const tvDe = (chave) => !!(naTV && naTV.chave === chave);

  // ---- Expediente ----
  const dadosTVItem = (it) => ({ tipo: 'EXPEDIENTE', chave: 'item:' + it.id, rotulo: TIPO_ITEM[it.tipo] || 'Expediente', titulo: it.titulo, texto: it.tipo === 'MATERIA' ? '' : (it.texto || '') });
  const lerItem = (it) => { setLeitor({ tipo: 'item', id: it.id }); mostrarNaTV(dadosTVItem(it)); };
  const marcarLido = async (it, lido = true) => {
    setOcupado(true);
    try {
      const r = await axios.patch(`/api/leituras/expediente/${it.id}/lido`, { lido });
      setItens(prev => prev.map(x => (x.id === it.id ? r.data : x)));
      return true;
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Não foi possível marcar.'); return false; }
    finally { setOcupado(false); }
  };
  const lidoEProximo = async (it) => {
    if (!(await marcarLido(it, true))) return;
    const pos = itens.findIndex(x => x.id === it.id);
    const prox = itens.slice(pos + 1).find(x => !x.lidoEm) || itens.find(x => !x.lidoEm && x.id !== it.id);
    if (prox) { lerItem(prox); toast.success('Lido. Próximo item.'); }
    else { fecharLeitor(true); setConcluido(true); } // PLENARIUM:EXPEDIENTE_CONCLUIDO
  };

  // ---- Materias ----
  const materias = pautas.filter(aberta).filter(p => p.tipo !== 'ATA_ANTERIOR');
  // PLENARIUM:FRASE_MATERIA -- a frase de leitura (tipo, numero, autor e ementa)
  const fraseDe = (p, d) => { const pr = d && d.projeto; return fraseMateria({ tipo: TIPO_PAUTA[(pr && pr.tipo) || p.tipo] || 'Matéria', numero: (pr && pr.numero) || p.numero, ano: pr && pr.ano, autor: pr && pr.autor, ementa: (pr && pr.ementa) || p.titulo }, vereadoresF); };
  const dadosTVMateria = (p, d0) => { const d = d0 || integra[p.id]; const pr = d && d.projeto; return { tipo: 'MATERIA', chave: 'pauta:' + p.id, rotulo: `${TIPO_PAUTA[p.tipo] || 'Matéria'} · ${p.numero}`, titulo: p.titulo, texto: pr && pr.autor ? `Autoria: ${autoriaFrase(pr.autor, vereadoresF).replace(/^de autoria (d[oa]s?|de)\s+/i, '').replace(/^de iniciativa popular/i, 'Iniciativa popular')}` : '' }; };
  const buscarIntegra = (p) => {
    if (integra[p.id]) return Promise.resolve(integra[p.id]);
    return axios.get(`/api/leituras/pauta/${p.id}/integra`).then(r => { setIntegra(prev => ({ ...prev, [p.id]: r.data })); return r.data; }).catch(() => { setIntegra(prev => ({ ...prev, [p.id]: { erro: true } })); return null; });
  };
  const lerMateria = (p) => { setLeitor({ tipo: 'materia', id: p.id }); setModoMateria('titulo'); buscarIntegra(p).then(d => mostrarNaTV(dadosTVMateria(p, d))); };

  const fecharLeitor = (tirar = true) => {
    const chave = leitor ? (leitor.tipo === 'item' ? 'item:' : 'pauta:') + leitor.id : null;
    setLeitor(null);
    if (tirar && chave && tvDe(chave)) tirarDaTV();
  };

  // ---- Ata ----
  const ataChave = ata && ata.pauta ? `ata:${ata.pauta.id}` : 'ata'; // PLENARIUM:ATAS_VARIAS
  const trocarAta = (i) => { if (tvDe(ataChave)) tirarDaTV(); setAtaIdx(i); setDocAta(0); };
  const ataTitulo = ata && ata.projeto ? (ata.projeto.ementa || `Ata ${ata.projeto.numero}/${ata.projeto.ano}`) : (ata && ata.pauta ? ata.pauta.titulo : '');
  const docsDe = (d, projetoId) => {
    const l = [];
    if (!d || !projetoId) return l;
    if (d.temPrincipal) l.push({ k: 'p', titulo: 'Texto principal', url: `/api/projetos-camara/${projetoId}/principal` });
    (d.anexos || []).forEach(a => l.push({ k: a.id, titulo: ANEXO[a.tipo] || 'Documento', sub: a.nome, url: `/api/projetos-camara/anexo/${a.id}` }));
    return l;
  };
  const docsAta = ata && ata.projeto ? docsDe(ata, ata.projeto.id) : []; // PLENARIUM:LEITOR_PDF
  const docSel = docAta < docsAta.length ? docAta : 0;

  if (!sessaoId) {
    return (
      <div className="pls"><style>{CSS}</style>
        <Cabecalho cargo={cargoMesa(cargo || (usuario && usuario.cargo), usuario && usuario.sexo)} sessao={null} />
        <div className="pls-vazio">As leituras aparecem aqui quando houver sessão em andamento.</div>
      </div>
    );
  }

  const lidos = itens.filter(x => x.lidoEm).length;
  const itemAberto = leitor && leitor.tipo === 'item' ? itens.find(x => x.id === leitor.id) : null;
  const materiaAberta = leitor && leitor.tipo === 'materia' ? pautas.find(x => x.id === leitor.id) : null;

  return (
    <div className="pls"><style>{CSS}</style>
      {/* >>> PLENARIUM:EXPEDIENTE_CONCLUIDO -- ao ler o ultimo item: tela de conclusao com a frase para o Presidente */}
      {concluido && (
        <div className="pls-leitor" style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24, background: 'linear-gradient(160deg, #ecfdf5 0%, #fffdf7 70%)' }}>
          <div style={{ width: 92, height: 92, borderRadius: '50%', background: '#16a34a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(22,163,74,.35)' }}><Check size={50} /></div>
          <h2 style={{ margin: '22px 0 6px', fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 900, color: '#14532d', lineHeight: 1.15 }}>Leitura do Expediente concluída</h2>
          <div style={{ color: '#475569', fontWeight: 700 }}>{itens.filter(x => x.lidoEm).length} de {itens.length} {itens.length === 1 ? 'item lido' : 'itens lidos'}</div>
          <div style={{ marginTop: 26, maxWidth: 640, padding: '18px 24px', borderRadius: 16, background: '#fff', border: '2px solid #bbf7d0', boxShadow: '0 8px 24px rgba(15,23,42,.06)' }}>
            <div style={{ fontSize: '.75rem', letterSpacing: 2, color: '#16a34a', fontWeight: 800, marginBottom: 8 }}>DIGA AO PRESIDENTE</div>
            <div style={{ fontSize: 'clamp(1.25rem, 3vw, 1.7rem)', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>“Senhor(a) Presidente, está concluída a leitura do Expediente.”</div>
          </div>
          <button type="button" className="pls-bt pls-bt-verde" style={{ marginTop: 28, minHeight: 56, minWidth: 220, fontSize: '1.05rem' }} onClick={() => setConcluido(false)}><Check size={20} /> Ok, entendi</button>
        </div>
      )}
      {/* <<< PLENARIUM:EXPEDIENTE_CONCLUIDO */}
      <Cabecalho cargo={cargoMesa(cargo || (usuario && usuario.cargo), usuario && usuario.sexo)} sessao={sessao} />

      {naTV && (
        <div className="pls-natv">
          <span className="pls-natv-ic"><Tv size={20} /></span>
          <span className="pls-natv-txt"><small>NA TV AGORA</small><span>{naTV.titulo}</span></span>
          <button type="button" className="pls-bt pls-bt-claro" onClick={tirarDaTV}><X size={16} /> Tirar da TV</button>
        </div>
      )}

      <nav className="pls-passos">
        <button type="button" className={`pls-passo ${passo === 'chamada' ? 'ativo' : ''}`} onClick={() => setPasso('chamada')}>
          <span className="pls-num">1</span><span><b>Chamada</b><small>Presença dos vereadores</small></span>
        </button>
        <button type="button" className={`pls-passo ${passo === 'ata' ? 'ativo' : ''}`} onClick={() => setPasso('ata')}>
          <span className="pls-num">2</span><span><b>Ata anterior</b><small>Logo após abrir a sessão</small></span>
        </button>
        <button type="button" className={`pls-passo ${passo === 'expediente' ? 'ativo' : ''}`} onClick={() => setPasso('expediente')}>
          <span className="pls-num">3</span><span><b>Expediente</b><small>{itens.length ? `${lidos} de ${itens.length} lidos` : 'Matérias, comunicados, convites'}</small></span>
        </button>
        <button type="button" className={`pls-passo ${passo === 'ordem' ? 'ativo' : ''}`} onClick={() => setPasso('ordem')}>
          <span className="pls-num">4</span><span><b>Ordem do Dia</b><small>{pautaAtiva ? 'Há matéria em votação' : 'Matérias para votação'}</small></span>
        </button>
      </nav>

      {passo === 'chamada' && (
        <div>
          <ChamadaSecretario sessao={sessao} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="pls-bt" onClick={() => setPasso('ata')}>Próximo: Ata anterior <ChevronRight size={18} /></button>
          </div>
        </div>
      )}

      {passo === 'ata' && (
        <div>
          <p className="pls-dica">{varias ? `Há ${atasLista.length} atas nesta sessão: leia uma de cada vez (cada uma tem a sua votação).` : 'Leia a ata da sessão anterior logo após a abertura.'} O documento anexado no Trâmite abre aqui embaixo; use <b>Tela cheia</b> e a lupa para aumentar a letra.</p>
          {varias && ( /* PLENARIUM:ATAS_VARIAS */
            <div className="pls-docs" style={{ marginBottom: 12 }}>
              {atasLista.map((a, i) => (
                <button key={(a.pauta && a.pauta.id) || i} type="button" className={`pls-bt ${i === ataIdx ? 'pls-bt-azul' : ''}`} onClick={() => trocarAta(i)}><FileText size={18} /> {i + 1}. {tituloCurto(a)}</button>
              ))}
            </div>
          )}
          {!ata ? <div className="pls-vazio">Carregando…</div>
            : ata.erro ? <div className="pls-vazio">Não foi possível buscar a ata.</div>
            : !ata.projeto ? (
              <div className="pls-vazio">Nenhuma ata da sessão anterior foi cadastrada no Trâmite.<br />Peça à secretaria para anexar o documento da ata.</div>
            ) : (
              <div className="pls-cartao">
                <span className="pls-chip" style={{ color: '#0f766e', background: '#ccfbf1' }}>ATA DA SESSÃO ANTERIOR</span>
                <h3 style={{ marginTop: 10 }}>{ataTitulo}</h3>
                <div style={{ color: '#64748b' }}>Nº {ata.projeto.numero}/{ata.projeto.ano}</div>
                {ata.pauta && ata.pauta.retificacao && <div className="pls-aviso" style={{ whiteSpace: 'pre-wrap' }}><b>Retificação:</b>{'\n'}{ata.pauta.retificacao}</div>}
                {/* PLENARIUM:LEITOR_PDF -- a ata e lida aqui mesmo, sem abrir outra aba */}
                <div className="pls-docs">
                  {docsAta.length > 1 && docsAta.map((d, i) => (
                    <button key={d.k} type="button" className={`pls-bt ${i === docSel ? 'pls-bt-azul' : ''}`} onClick={() => setDocAta(i)}><FileText size={18} /> {i === 0 ? 'Ata' : d.titulo}</button>
                  ))}
                  {tvDe(ataChave)
                    ? <button type="button" className="pls-bt pls-bt-tv" onClick={tirarDaTV}><Check size={18} /> Na TV · tirar</button>
                    : <button type="button" className="pls-bt" onClick={() => mostrarNaTV({ tipo: 'ATA', chave: ataChave, rotulo: 'Ata da Sessão Anterior', titulo: ataTitulo })}><Tv size={18} /> Mostrar na TV</button>}
                </div>
                {docsAta.length
                  ? <LeitorPDF url={docsAta[docSel].url} titulo={docSel === 0 ? ataTitulo : docsAta[docSel].titulo} />
                  : <div className="pls-aviso">A ata está no Trâmite, mas ainda sem documento anexado.</div>}
              </div>
            )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            {varias && ataIdx < atasLista.length - 1 /* PLENARIUM:ATAS_VARIAS */
              ? <button type="button" className="pls-bt" onClick={() => trocarAta(ataIdx + 1)}>Próxima ata ({ataIdx + 2} de {atasLista.length}) <ChevronRight size={18} /></button>
              : <button type="button" className="pls-bt" onClick={() => { if (tvDe(ataChave)) tirarDaTV(); setPasso('expediente'); }}>Próximo: Expediente <ChevronRight size={18} /></button>}
          </div>
        </div>
      )}

      {passo === 'expediente' && (
        <div>
          <p className="pls-dica">Toque em <b>Ler agora</b>: o texto abre em letra grande e o item aparece na TV. Ao terminar, toque em <b>Lido</b> e o próximo item já abre.</p>
          {!itens.length ? <div className="pls-vazio">Nenhum item no Expediente desta sessão.<br />A secretaria cadastra os itens na tela da sessão.</div> : (
            <div className="pls-lista">
              {itens.map((it, i) => {
                const [cor, fundo] = COR_ITEM[it.tipo] || COR_ITEM.OUTRO;
                return (
                  <div key={it.id} className={`pls-item ${it.lidoEm ? 'lido' : ''}`}>
                    <span className="pls-ordem">{it.lidoEm ? <Check size={16} /> : i + 1}</span>
                    <span className="pls-item-txt">
                      <span className="pls-chip" style={{ color: cor, background: fundo }}>{(TIPO_ITEM[it.tipo] || 'Item').toUpperCase()}</span>
                      {it.lidoEm && <span className="pls-chip" style={{ color: '#15803d', background: '#dcfce7' }}>LIDO</span>}
                      {tvDe('item:' + it.id) && <span className="pls-chip" style={{ color: '#fff', background: '#0f2a5f' }}>NA TV</span>}
                      <span className="pls-item-tit">{it.titulo}</span>
                    </span>
                    <button type="button" className={`pls-bt ${it.lidoEm ? '' : 'pls-bt-azul'}`} onClick={() => lerItem(it)}><BookOpen size={18} /> {it.lidoEm ? 'Ler de novo' : 'Ler agora'}</button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, gap: 10 }}>
            <button type="button" className="pls-bt" onClick={() => setPasso('ata')}><ChevronLeft size={18} /> Ata anterior</button>
            <button type="button" className="pls-bt" onClick={() => setPasso('ordem')}>Próximo: Ordem do Dia <ChevronRight size={18} /></button>
          </div>
        </div>
      )}

      {passo === 'ordem' && (
        <div>
          <p className="pls-dica">Antes de cada votação, abra a matéria e escolha <b>Só o título</b> ou <b>Na íntegra</b>. A matéria aparece na TV enquanto você lê.</p>
          {!materias.length ? <div className="pls-vazio">Nenhuma matéria na Ordem do Dia.</div> : (
            <div className="pls-lista">
              {materias.map((p, i) => {
                const votando = p.status === 'EM_VOTACAO';
                const votada = p.status === 'APROVADA' || p.status === 'REPROVADA';
                return (
                  <div key={p.id} className={`pls-item ${votada ? 'lido' : ''} ${votando ? 'votando' : ''}`}>
                    <span className="pls-ordem">{i + 1}</span>
                    <span className="pls-item-txt">
                      <span className="pls-chip" style={{ color: '#1d4ed8', background: '#dbeafe' }}>{(TIPO_PAUTA[p.tipo] || 'Matéria').toUpperCase()} · {p.numero}</span>
                      <span className="pls-chip" style={votando ? { color: '#fff', background: '#dc2626' } : votada ? { color: '#15803d', background: '#dcfce7' } : { color: '#475569', background: '#f1f5f9' }}>{votando ? <><Vote size={11} style={{ verticalAlign: '-1px' }} /> EM VOTAÇÃO</> : (STATUS_PAUTA[p.status] || p.status).toUpperCase()}</span>
                      {tvDe('pauta:' + p.id) && <span className="pls-chip" style={{ color: '#fff', background: '#0f2a5f' }}>NA TV</span>}
                      <span className="pls-item-tit">{p.titulo}</span>
                    </span>
                    <button type="button" className={`pls-bt ${votada ? '' : 'pls-bt-azul'}`} onClick={() => lerMateria(p)}><BookOpen size={18} /> Ler</button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 14 }}>
            <button type="button" className="pls-bt" onClick={() => setPasso('expediente')}><ChevronLeft size={18} /> Expediente</button>
          </div>
        </div>
      )}

      {/* ---------- Leitor do Expediente ---------- */}
      {itemAberto && (
        <Leitor rotulo={TIPO_ITEM[itemAberto.tipo] || 'Expediente'} fonte={fonte} setFonte={setFonte} onVoltar={() => fecharLeitor(true)}
          base={<>
            {tvDe('item:' + itemAberto.id)
              ? <button type="button" className="pls-bt pls-bt-tv" onClick={tirarDaTV}><Check size={18} /> Na TV · tirar</button>
              : <button type="button" className="pls-bt" onClick={() => mostrarNaTV(dadosTVItem(itemAberto))}><Tv size={18} /> Mostrar na TV</button>}
            {itemAberto.lidoEm
              ? <button type="button" className="pls-bt" disabled={ocupado} onClick={() => marcarLido(itemAberto, false)}><X size={18} /> Desmarcar lido</button>
              : <button type="button" className="pls-bt pls-bt-verde" disabled={ocupado} onClick={() => lidoEProximo(itemAberto)}><Check size={20} /> Lido · próximo</button>}
          </>}>
          <h2 style={{ fontSize: Math.round(fonte * 1.15) }}>{itemAberto.titulo}</h2>
          {itemAberto.texto ? <div className="pls-texto" style={{ fontSize: fonte }}>{itemAberto.texto}</div>
            : <div style={{ color: '#64748b', fontSize: 18 }}>Sem texto: leia o título acima.</div>}
          {itemAberto.projetoId && <DocsProjeto projetoId={itemAberto.projetoId} />}
        </Leitor>
      )}

      {/* ---------- Leitor da materia (Ordem do Dia) ---------- */}
      {materiaAberta && (() => {
        const d = integra[materiaAberta.id];
        const proj = d && d.projeto;
        const textoIntegra = (proj && proj.descricao) || (d && d.pauta && d.pauta.descricao) || materiaAberta.descricao || '';
        return (
          <Leitor rotulo={`${TIPO_PAUTA[materiaAberta.tipo] || 'Matéria'} · ${materiaAberta.numero}`} fonte={fonte} setFonte={setFonte} onVoltar={() => fecharLeitor(true)}
            base={<>
              {tvDe('pauta:' + materiaAberta.id)
                ? <button type="button" className="pls-bt pls-bt-tv" onClick={tirarDaTV}><Check size={18} /> Na TV · tirar</button>
                : <button type="button" className="pls-bt" onClick={() => mostrarNaTV(dadosTVMateria(materiaAberta))}><Tv size={18} /> Mostrar na TV</button>}
              <button type="button" className="pls-bt pls-bt-verde" onClick={() => fecharLeitor(true)}><Check size={20} /> Leitura concluída</button>
            </>}>
            <div className="pls-seg">
              <button type="button" className={modoMateria === 'titulo' ? 'ativo' : ''} onClick={() => setModoMateria('titulo')}>Só o título</button>
              <button type="button" className={modoMateria === 'integra' ? 'ativo' : ''} onClick={() => setModoMateria('integra')}>Na íntegra</button>
            </div>
            {/* PLENARIUM:FRASE_MATERIA -- a materia ja na forma de ler: tipo, numero, autoria e ementa */}
            <h2 style={{ fontSize: Math.round(fonte * 1.15) }}>{d && !d.erro ? fraseDe(materiaAberta, d) : materiaAberta.titulo}</h2>
            {modoMateria === 'integra' && (
              !d ? <div style={{ color: '#64748b' }}>Carregando o texto…</div>
                : d.erro ? <div style={{ color: '#b91c1c' }}>Não foi possível buscar o texto.</div>
                : <>
                  {textoIntegra ? <div className="pls-texto" style={{ fontSize: fonte }}>{textoIntegra}</div>
                    : <div style={{ color: '#64748b', fontSize: 18 }}>Sem texto cadastrado. Abra o documento abaixo.</div>}
                  {proj && <DocsLista docs={docsDe(d, proj.id)} />}
                  {proj && <PareceresResumo projetoId={proj.id} grande />} {/* PLENARIUM:COMISSOES */}
                </>
            )}
          </Leitor>
        );
      })()}
    </div>
  );
}

function Cabecalho({ cargo, sessao }) {
  return (
    <div className="pls-cab">
      <span className="pls-cab-ic"><ScrollText size={28} /></span>
      <span><b>Leituras da Mesa</b><small>{cargo}{sessao ? ` · ${sessao.titulo}` : ''}</small></span>
    </div>
  );
}

function Leitor({ rotulo, fonte, setFonte, onVoltar, base, children }) {
  return (
    <div className="pls-leitor" role="dialog" aria-label="Leitura">
      <div className="pls-leitor-topo">
        <button type="button" className="pls-bt pls-bt-claro" onClick={onVoltar}><ChevronLeft size={18} /> Voltar</button>
        <span className="pls-rot"><Megaphone size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{rotulo}</span>
        <span className="pls-zoom">
          <button type="button" className="pls-bt pls-bt-claro" aria-label="Diminuir letra" onClick={() => setFonte(f => Math.max(16, f - 2))}><Minus size={16} /> A</button>
          <button type="button" className="pls-bt pls-bt-claro" aria-label="Aumentar letra" onClick={() => setFonte(f => Math.min(48, f + 2))}><Plus size={16} /> A</button>
        </span>
      </div>
      <div className="pls-leitor-corpo">{children}</div>
      <div className="pls-leitor-base">{base}</div>
    </div>
  );
}

function DocsLista({ docs }) {
  if (!docs || !docs.length) return null;
  return (
    <div className="pls-docs">
      {docs.map(d => <button key={d.k} type="button" className="pls-bt" onClick={() => abrirDocumento(d.url)}><FileText size={18} /> {d.titulo}</button>)}
    </div>
  );
}

function DocsProjeto({ projetoId }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    let vivo = true;
    axios.get(`/api/projetos-camara/${projetoId}/documentos`).then(r => {
      if (!vivo) return;
      const l = [];
      if (r.data && r.data.temPrincipal) l.push({ k: 'p', titulo: 'Texto da matéria', url: `/api/projetos-camara/${projetoId}/principal` });
      ((r.data && r.data.anexos) || []).forEach(a => l.push({ k: a.id, titulo: ANEXO[a.tipo] || 'Documento', url: `/api/projetos-camara/anexo/${a.id}` }));
      setDocs(l);
    }).catch(() => { if (vivo) setDocs([]); });
    return () => { vivo = false; };
  }, [projetoId]);
  return <DocsLista docs={docs} />;
}
